import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type AccessRequest } from '../api.ts';
import { localized } from '../i18n/index.ts';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Loading,
  PageHeader,
  Table,
} from '../components/ui.tsx';
import { ReasonDialog } from '../components/FormModal.tsx';
import { MasterDeleteButton } from '../components/MasterDelete.tsx';
import { ConfirmDialog } from '../components/Modal.tsx';
import { TierBadge } from './Roles.tsx';
import { useMe } from '../auth.tsx';

/**
 * Pedidos de acesso (ADR 0042): concessão de perfil sensível, restrito ou crítico e retirada de
 * perfil crítico. Quem pede, quem aprova e quem recebe são pessoas diferentes; quem aprova é um
 * Administrador, e a aprovação já concede (ou retira) o perfil.
 */
export function AccessRequestsPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  // Fase de desenvolvimento (ADR 0053): o master aprova também os próprios pedidos.
  const devPhase = !!useMe().data?.master?.exceptionUntil;
  // Aprovar e recusar abrem em janela (guia visual, Modal), nunca no navegador.
  const [approving, setApproving] = useState<AccessRequest | null>(null);
  const [rejecting, setRejecting] = useState<AccessRequest | null>(null);
  const list = useQuery({
    queryKey: ['access-requests'],
    queryFn: () => api.get<AccessRequest[]>('/iam/access-requests'),
  });
  const done = async () => {
    await qc.invalidateQueries({ queryKey: ['access-requests'] });
    await qc.invalidateQueries({ queryKey: ['users'] });
  };
  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/iam/access-requests/${id}/approve`, {}),
    onSuccess: done,
  });
  const reject = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      api.post(`/iam/access-requests/${id}/reject`, { note }),
    onSuccess: async () => {
      setRejecting(null);
      await done();
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api.post(`/iam/access-requests/${id}/cancel`, {}),
    onSuccess: done,
  });
  const dateTime = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
  const status = (r: AccessRequest) => {
    if (r.expired) return <Badge tone="off">{t('access.status.expired')}</Badge>;
    const tone = r.status === 'approved' ? 'ok' : r.status === 'pending' ? 'info' : 'off';
    return <Badge tone={tone}>{t(`access.status.${r.status}`)}</Badge>;
  };

  return (
    <>
      <PageHeader title={t('access.title')} description={t('access.description')} />
      {devPhase && <p className="text-sm text-muted">{t('access.devPhaseNote')}</p>}
      {/* Com a janela de recusa aberta, o erro dela aparece nela. */}
      <ErrorBox error={approve.error ?? (rejecting ? null : reject.error) ?? cancel.error} />
      <ConfirmDialog
        open={approving !== null}
        title={t('access.approveTitle')}
        description={
          approving ? t('access.approveConfirm', { name: approving.userName }) : undefined
        }
        confirmLabel={t('access.approveTitle')}
        busy={approve.isPending}
        onCancel={() => setApproving(null)}
        onConfirm={() => {
          if (approving) approve.mutate(approving.id);
          setApproving(null);
        }}
      />
      <ReasonDialog
        open={rejecting !== null}
        title={t('access.rejectTitle')}
        description={rejecting ? t('access.rejectText', { name: rejecting.userName }) : undefined}
        fieldLabel={t('access.rejectReason')}
        confirmLabel={t('access.rejectTitle')}
        tone="danger"
        pending={reject.isPending}
        error={reject.error}
        onConfirm={(note) => rejecting && reject.mutate({ id: rejecting.id, note })}
        onClose={() => {
          setRejecting(null);
          reject.reset();
        }}
      />
      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorBox error={list.error} />
      ) : (
        <Table
          head={[
            t('access.when'),
            t('access.kind'),
            t('access.who'),
            t('users.role'),
            t('access.requestedBy'),
            t('access.justification'),
            t('fields.status'),
            '',
          ]}
        >
          {list.data.length === 0 && <EmptyRow colSpan={8}>{t('access.empty')}</EmptyRow>}
          {list.data.map((r) => (
            <tr key={r.id}>
              <td className="whitespace-nowrap text-xs">
                {dateTime.format(new Date(r.requestedAt))}
              </td>
              <td className="text-xs">{t(`access.kinds.${r.kind}`)}</td>
              <td className="text-xs">{r.userName}</td>
              <td className="text-xs">
                {r.roleName ? localized(r.roleName) : t(`access.noRole.${r.kind}`)}
                <div className="mt-1">
                  <TierBadge tier={r.tier} />
                </div>
              </td>
              <td className="text-xs">{r.requestedByName}</td>
              <td className="max-w-xs text-xs">
                {r.justification}
                {r.decisionNote && (
                  <div className="mt-1 text-muted">
                    {r.decidedByName}: {r.decisionNote}
                  </div>
                )}
              </td>
              <td>{status(r)}</td>
              <td className="text-right">
                <div className="flex justify-end gap-2">
                  {r.canDecide && (
                    <>
                      <Button disabled={approve.isPending} onClick={() => setApproving(r)}>
                        {t('access.approve')}
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={reject.isPending}
                        onClick={() => setRejecting(r)}
                      >
                        {t('access.reject')}
                      </Button>
                    </>
                  )}
                  {r.canCancel && (
                    <Button
                      variant="secondary"
                      disabled={cancel.isPending}
                      onClick={() => cancel.mutate(r.id)}
                    >
                      {t('access.cancel')}
                    </Button>
                  )}
                  <MasterDeleteButton table="iam.access_request" id={r.id} compact />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}
